import React from 'react';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * ClickTrackOverlay - SVG overlay for trajectory paths with instant click feedback
 *
 * Sport-agnostic component that:
 * - Shows immediate visual feedback on click (< 16ms pulsing dot)
 * - Renders trajectory paths as SVG cubic bezier curves
 * - Displays confidence-based opacity
 * - Handles loading, success, and error states per click marker
 */
const ClickTrackOverlay = ({
  trajectories = [],
  clickMarkers = [],
  onVideoClick,
  videoRef
}) => {
  const extractOverlayPoint = (pointLike) => {
    if (!pointLike) return null;

    const source = Array.isArray(pointLike) ? (pointLike[1] || pointLike[0]) : pointLike;
    if (!source || typeof source !== 'object') return null;

    const nestedPoint = source.position_2d || source.point || source;
    const u = Number(nestedPoint.u);
    const v = Number(nestedPoint.v);
    const confidence = Number(source.confidence ?? nestedPoint.confidence ?? 0.5);

    if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

    const normalizedU = u > 1 ? (u / 1920) : u;
    const normalizedV = v > 1 ? (v / 1080) : v;

    return {
      u: Math.max(0, Math.min(1, normalizedU)),
      v: Math.max(0, Math.min(1, normalizedV)),
      confidence: Number.isFinite(confidence) ? confidence : 0.5,
    };
  };

  /**
   * Generate cubic bezier path from trajectory points
   * Converts 3D positions → 2D projected coordinates for SVG path
   */
  const generateBezierPath = (points) => {
    if (!points || points.length === 0) return '';

    const mapped = points.map(extractOverlayPoint).filter(Boolean);
    if (mapped.length === 0) return '';

    // Start at first point
    let path = `M ${mapped[0].u * 100}% ${mapped[0].v * 100}%`;

    // Use cubic bezier for smooth curves
    for (let i = 1; i < mapped.length; i++) {
      const prev = mapped[i - 1];
      const curr = mapped[i];

      // Control points for smooth curve (1/3 distance)
      const cp1x = prev.u + (curr.u - prev.u) / 3;
      const cp1y = prev.v + (curr.v - prev.v) / 3;
      const cp2x = curr.u - (curr.u - prev.u) / 3;
      const cp2y = curr.v - (curr.v - prev.v) / 3;

      path += ` C ${cp1x * 100}% ${cp1y * 100}%, ${cp2x * 100}% ${cp2y * 100}%, ${curr.u * 100}% ${curr.v * 100}%`;
    }

    return path;
  };

  /**
   * Handle video click - normalize coordinates and call parent handler
   */
  const handleClick = (e) => {
    if (!videoRef?.current || !onVideoClick) return;

    const rect = videoRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Normalize to 0-1 range
    const normalizedX = x / rect.width;
    const normalizedY = y / rect.height;

    onVideoClick({ u: normalizedX, v: normalizedY }, e);
  };

  return (
    <>
      {/* Click handler overlay - allows clicks but doesn't block video */}
      <div
        className="absolute inset-0 z-10 cursor-crosshair"
        onClick={handleClick}
        style={{ pointerEvents: 'auto' }}
      />

      {/* SVG trajectory paths overlay - no pointer events */}
      <svg
        className="absolute inset-0 pointer-events-none overflow-hidden z-20"
        style={{ width: '100%', height: '100%' }}
      >
        {/* Render all trajectory paths */}
        {trajectories.map((trajectory, idx) => {
          const mappedPoints = (trajectory.points || []).map(extractOverlayPoint).filter(Boolean);
          const avgConfidence = mappedPoints.length > 0
            ? mappedPoints.reduce((sum, p) => sum + (p.confidence || 0), 0) / mappedPoints.length
            : 0.5;

          return (
            <g key={trajectory.id || idx}>
              {/* Main trajectory path */}
              <path
                d={generateBezierPath(mappedPoints)}
                stroke={`rgba(34, 211, 238, ${avgConfidence})`} // Cyan with confidence opacity
                strokeWidth={3}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Confidence markers at each point */}
              {mappedPoints.map((point, pidx) => (
                <circle
                  key={pidx}
                  cx={`${point.u * 100}%`}
                  cy={`${point.v * 100}%`}
                  r={4}
                  fill={`rgba(34, 211, 238, ${point.confidence || 0.5})`}
                  stroke="white"
                  strokeWidth={1}
                />
              ))}
            </g>
          );
        })}
      </svg>

      {/* Click markers overlay - shows instant feedback */}
      <div className="absolute inset-0 pointer-events-none z-30">
        {clickMarkers.map((marker) => {
          const posX = `${marker.point.u * 100}%`;
          const posY = `${marker.point.v * 100}%`;

          return (
            <div
              key={marker.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2"
              style={{ left: posX, top: posY }}
            >
              {/* Status-based marker */}
              {marker.status === 'analyzing' && (
                <>
                  {/* Pulsing cyan dot */}
                  <div className="w-4 h-4 rounded-full bg-cyan-500 animate-pulse border-2 border-white shadow-lg" />

                  {/* "Analyzing..." label */}
                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                    <div className="bg-black/90 px-2 py-1 rounded flex items-center gap-1.5">
                      <Loader2 className="text-cyan-400 animate-spin" size={12} />
                      <span className="text-xs text-cyan-400 font-medium">Analyzing...</span>
                    </div>
                  </div>
                </>
              )}

              {marker.status === 'success' && (
                <>
                  {/* Success green dot */}
                  <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow-lg" />

                  {/* Checkmark */}
                  <div className="absolute -top-1 -right-1">
                    <CheckCircle className="text-green-400" size={14} fill="white" />
                  </div>
                </>
              )}

              {marker.status === 'error' && (
                <>
                  {/* Error red dot */}
                  <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow-lg" />

                  {/* Error icon */}
                  <div className="absolute -top-1 -right-1">
                    <AlertCircle className="text-red-400" size={14} fill="white" />
                  </div>

                  {/* Error code label if available */}
                  {marker.errorCode && (
                    <div className="absolute top-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                      <div className="bg-red-500/20 border border-red-500/50 px-2 py-1 rounded">
                        <span className="text-xs text-red-300">Error {marker.errorCode}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ClickTrackOverlay;
